import { vec3 } from "gl-matrix";

import { initShaderProgram } from "./gl-utils";
import Blur from "./blur";

const lerp = (a, b, t) => a * (1 - t) + b * t;

export default class Ssao {
  constructor(gl) {
    this.samples = new Float32Array(64 * 3);
    for (let i = 0; i < 64; i++) {
      let v = [
        Math.random() * 2.0 - 1.0,
        Math.random() * 2.0 - 1.0,
        Math.random(),
      ];
      vec3.normalize(v, v);

      let scale = i / 64.0;
      scale = lerp(0.1, 1.0, scale * scale);

      this.samples[3 * i] = v[0] * scale;
      this.samples[3 * i + 1] = v[1] * scale;
      this.samples[3 * i + 2] = v[2] * scale;
    }

    // Program
    const program = initShaderProgram(
      gl,
      require("./shaders/splash.vert").default,
      require("./shaders/ssao.frag").default
    );
    this.programInfo = {
      program: program,
      attribLocations: {},
      uniformLocations: {
        projection: gl.getUniformLocation(program, "u_Projection"),
        gPosition: gl.getUniformLocation(program, "u_gPosition"),
        gNormal: gl.getUniformLocation(program, "u_gNormal"),
        noise: gl.getUniformLocation(program, "u_Noise"),
        samples: gl.getUniformLocation(program, "u_Samples[0]"),
      },
    };

    this.fbo = gl.createFramebuffer();
    gl.bindFramebuffer(gl.DRAW_FRAMEBUFFER, this.fbo);

    // Noise texture
    const pixels = new Float32Array(16 * 3);
    for (let i = 0; i < 16; i++) {
      pixels[3 * i] = Math.random() * 2.0 - 1.0;
      pixels[3 * i + 1] = Math.random() * 2.0 - 1.0;
      pixels[3 * i + 2] = 0.0;
    }

    this.noise = gl.createTexture();
    gl.bindTexture(gl.TEXTURE_2D, this.noise);

    gl.texImage2D(
      gl.TEXTURE_2D, // target
      0, // level
      gl.RGB32F, // internal_format
      4, // width
      4, // height
      0, // border
      gl.RGB, // format
      gl.FLOAT, // ty
      pixels // pixels
    );

    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.NEAREST);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.NEAREST);

    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.REPEAT);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.REPEAT);

    gl.bindTexture(gl.TEXTURE_2D, null);

    // Output texture
    this.texture = gl.createTexture();
    gl.bindTexture(gl.TEXTURE_2D, this.texture);

    gl.texImage2D(
      gl.TEXTURE_2D, // target
      0, // level
      gl.R16F, // internal_format
      gl.canvas.width, // width
      gl.canvas.height, // height
      0, // border
      gl.RED, // format
      gl.FLOAT, // ty
      null // pixels
    );

    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_BASE_LEVEL, 0);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAX_LEVEL, 4);

    gl.texParameteri(
      gl.TEXTURE_2D,
      gl.TEXTURE_MIN_FILTER,
      gl.LINEAR_MIPMAP_LINEAR
    );
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.LINEAR);

    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_R, gl.CLAMP_TO_EDGE);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);

    gl.framebufferTexture2D(
      gl.DRAW_FRAMEBUFFER, // target
      gl.COLOR_ATTACHMENT0, // attachment
      gl.TEXTURE_2D, // texture_target
      this.texture, // texture
      0 // level
    );

    gl.bindTexture(gl.TEXTURE_2D, null);

    gl.bindFramebuffer(gl.DRAW_FRAMEBUFFER, null);

    // Blur
    this.blur = new Blur(gl);
  }

  draw(gl, gbuffer, projection) {
    gl.bindFramebuffer(gl.DRAW_FRAMEBUFFER, this.fbo);
    gl.clear(gl.COLOR_BUFFER_BIT);
    gl.disable(gl.DEPTH_TEST);

    gl.useProgram(this.programInfo.program);

    gl.uniformMatrix4fv(
      this.programInfo.uniformLocations.projection,
      false,
      projection
    );

    gl.activeTexture(gl.TEXTURE0);
    gl.bindTexture(gl.TEXTURE_2D, gbuffer.outputPosition);
    gl.uniform1i(this.programInfo.uniformLocations.gPosition, 0);

    gl.activeTexture(gl.TEXTURE1);
    gl.bindTexture(gl.TEXTURE_2D, gbuffer.outputNormal);
    gl.uniform1i(this.programInfo.uniformLocations.gNormal, 1);

    gl.activeTexture(gl.TEXTURE2);
    gl.bindTexture(gl.TEXTURE_2D, this.noise);
    gl.uniform1i(this.programInfo.uniformLocations.noise, 2);

    gl.uniform3fv(this.programInfo.uniformLocations.samples, this.samples);

    gl.drawArrays(gl.TRIANGLES, 0, 6);

    // Re-generate mipmaps
    gl.bindTexture(gl.TEXTURE_2D, this.texture);
    gl.generateMipmap(gl.TEXTURE_2D);
    gl.bindTexture(gl.TEXTURE_2D, null);

    gl.enable(gl.DEPTH_TEST);
    gl.bindFramebuffer(gl.DRAW_FRAMEBUFFER, null);

    // this.blur.apply(gl, this.texture);
  }

  cleanup(gl) {
    this.blur.cleanup(gl);

    gl.deleteFramebuffer(this.fbo);
    gl.deleteTexture(this.noise);
    gl.deleteTexture(this.texture);
  }
}
